import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import cn from 'classnames';
import { MdNavigateBefore, MdNavigateNext } from 'react-icons/md';
import { RadioGroup } from '@headlessui/react';
import { useFormikContext } from 'formik';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { DateField, Input, PhoneNumber } from 'ui/components/form';
import { Button } from 'ui/components/common';
import { useGetBookingAvailabilityDatesAndTimes } from 'modules/account/calendar/hooks';
import RichTextDisplay from 'ui/components/common/RichTextDisplay';

import { BOOKING_DETAILS } from './ClientBooking';
import { CLIENT_BOOKING_FORM } from '../../forms/schema';

type PROPS = {
  bookingDetails: BOOKING_DETAILS;
};

const NoTimeAvailable: FC = () => (
  <div className="flex items-center w-full h-full">
    <div
      className={cn(
        'text-disabled',
        'font-normal',
        'text-sm',
        'text-center',
        'border',
        'border-separator-secondary',
        'rounded-md',
        'p-16',
      )}
    >
      We can’t find available time on that date.
    </div>
  </div>
);

const ClientBookingForm: FC<PROPS> = ({ bookingDetails }) => {
  const { id } = useParams();
  const {
    values,
    isValid,
    touched,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormikContext<CLIENT_BOOKING_FORM>();

  const _selectedDate = dayjs(values.selectedDate);
  const [selectedMonth, setSelectedMonth] = useState<string>(
    `${dayjs().month() + 1}`,
  );
  const [selectedYear, setSelectedYear] = useState(
    dayjs().format('YYYY').toLowerCase(),
  );

  const { data: datesTimes, isLoading: availableDateTimesLoading } =
    useGetBookingAvailabilityDatesAndTimes({
      id: id as string,
      requestCurrentUnixTime: dayjs().unix(),
      month: +selectedMonth,
      year: +selectedYear,
      requestingTimezone: dayjs.tz.guess(),
    });

  const availableDates: Date[] = useMemo(() => {
    return datesTimes?.dates.map?.((dt) => new Date(dt.date)) ?? [];
  }, [datesTimes]);

  const availableTimes = datesTimes?.dates.find((d) =>
    dayjs(d.date).isSame(dayjs(values.selectedDate)),
  )?.times;

  const filterDate = useCallback(
    (date: Date) => {
      return availableDates?.some((availableDate) =>
        dayjs(availableDate).isSame(dayjs(date), 'day'),
      );
    },
    [availableDates],
  );

  useEffect(() => {
    if (availableTimes && availableTimes.length) {
      if (!values.startAt) {
        setFieldValue('startAt', availableTimes[0]);
      }
    }
  }, [setFieldValue, availableTimes, values.startAt]);

  useEffect(() => {
    if (availableDates?.length) {
      if (!values.selectedDate) {
        setFieldValue('selectedDate', availableDates[0]);
      }
    }
  }, [availableDates, setFieldValue, values.selectedDate]);

  console.log(bookingDetails?.content);

  return (
    <form
      onSubmit={handleSubmit}
      className="shadow-leapify-sm max-w-[700px] space-y-4 rounded-[6px] bg-fill-tertiary p-8"
    >
      <div
        className={cn(
          'text-[22px]',
          'font-bold',
          'leading-7',
          'tracking-[0.88px]',
          'pb-4',
          'border-b-[0.5px]',
          'border-b-separator-primary',
        )}
      >
        Booking Invite
      </div>
      <div className="space-y-4 border-b-[0.5px] border-separator-primary pb-4">
        <p className="text-sm font-bold leading-[22px] tracking-[0.56px] text-info-base">
          Meeting Details
        </p>
        <div className="grid grid-cols-2 gap-x-5 gap-y-4">
          <div className="col-span-1">
            <p className="text-sm font-semibold text-content">Booking Title</p>
            <p className="text-sm font-bold text-secondary-dark">
              {bookingDetails?.title}
            </p>
          </div>
          <div className="col-span-1">
            <p className="text-sm font-semibold text-content">
              Booking Meeting Duration
            </p>
            <p className="text-sm font-bold text-secondary-dark">
              {bookingDetails?.duration} Minutes
            </p>
          </div>
          <div className="col-span-2">
            <p className="text-sm font-semibold text-content">
              Booking Organizer
            </p>
            <p className="text-sm font-bold text-secondary-dark">
              {bookingDetails?.organizer}
            </p>
          </div>
          <div className="col-span-2">
            <p className="text-sm font-semibold text-content">Description</p>
            <RichTextDisplay text={bookingDetails?.content ?? ''} />
          </div>
        </div>
      </div>
      <div className="space-y-4 border-b-[0.5px] border-separator-primary pb-4">
        <div className="text-sm font-bold leading-[22px] tracking-[0.56px] text-info-base">
          Attendee Details
        </div>
        <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
          <Input
            textType={
              errors.firstName && touched.firstName ? 'danger' : 'default'
            }
            onChange={handleChange}
            onBlur={handleBlur}
            subText={
              errors.firstName && touched.firstName ? errors.firstName : ''
            }
            name="firstName"
            type="text"
            className="w-full"
            label="First Name"
            required
            value={values.firstName}
            disabled={isSubmitting}
            placeholder="Enter your First Name"
          />
          <Input
            textType={
              errors.lastName && touched.lastName ? 'danger' : 'default'
            }
            onChange={handleChange}
            onBlur={handleBlur}
            subText={errors.lastName && touched.lastName ? errors.lastName : ''}
            name="lastName"
            type="text"
            className="w-full"
            label="Last Name"
            required
            value={values.lastName}
            disabled={isSubmitting}
            placeholder="Enter your Last Name"
          />
          <Input
            textType={
              errors.emailAddress && touched.emailAddress ? 'danger' : 'default'
            }
            onChange={handleChange}
            onBlur={handleBlur}
            subText={
              errors.emailAddress && touched.emailAddress
                ? errors.emailAddress
                : ''
            }
            name="emailAddress"
            type="text"
            className="w-full"
            label="Email"
            required
            value={values.emailAddress}
            disabled={isSubmitting}
            placeholder="Enter your Email Address"
          />
          <PhoneNumber
            className="w-full"
            name="contactNumber"
            label="Phone Number"
            value={values.contactNumber}
            onChange={setFieldValue}
            required
            textType={
              errors.contactNumber && touched.contactNumber
                ? 'danger'
                : 'default'
            }
            subText={
              errors.contactNumber && touched.contactNumber
                ? errors.contactNumber
                : ''
            }
            disabled={isSubmitting}
          />
          <div>
            <div
              className={cn(
                'font-bold',
                'leading-[22px]',
                'tracking-[0.56px]',
                'text-sm',
                'mb-[10px]',
              )}
            >
              Select a Date & Time
            </div>
            <div className={cn('text-xs', 'mb-[10px]')}>
              Available Dates and Times are in{' '}
              <strong className="underline">{bookingDetails.timezone}</strong>{' '}
              timezone.
            </div>
            <DateField
              name="selectedDate"
              selected={new Date(values.selectedDate as string)}
              inline
              filterDate={filterDate}
              disabled={availableDateTimesLoading}
              onMonthChange={(month) => {
                setSelectedMonth(dayjs(month).format('MM'));
                setSelectedYear(dayjs(month).format('YYYY'));
              }}
              onChange={(name, d) =>
                setFieldValue(
                  name as string,
                  dayjs(d as Date)
                    .startOf('day')
                    .toDate(),
                )
              }
              renderCustomHeader={({
                date,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }) => (
                <div className="flex items-center justify-between border-separator-primary py-4">
                  <p className="text-sm font-semibold">
                    {date.toLocaleString('default', {
                      month: 'long',
                    })}{' '}
                    {date.getFullYear()}
                  </p>
                  <div className="flex items-center gap-x-5">
                    <button
                      type="button"
                      onClick={decreaseMonth}
                      disabled={prevMonthButtonDisabled}
                      className="text-content"
                    >
                      <MdNavigateBefore className="text-lg" />
                    </button>
                    <button
                      type="button"
                      className={cn(
                        'text-primary-base transition',
                        'disabled:bg-transparent disabled:text-disabled',
                      )}
                      onClick={increaseMonth}
                      disabled={nextMonthButtonDisabled}
                    >
                      <MdNavigateNext className="text-lg" />
                    </button>
                  </div>
                </div>
              )}
            />
          </div>
          <div className="flex-auto">
            <div
              className={cn(
                'font-bold',
                'leading-[22px]',
                'tracking-[0.56px]',
                'text-sm',
                'mb-[10px]',
              )}
            >
              {_selectedDate.isValid() &&
                _selectedDate.format('dddd, MMMM D YYYY')}
            </div>
            {!availableTimes?.length ? (
              <NoTimeAvailable />
            ) : (
              <RadioGroup
                defaultValue={values?.startAt}
                defaultChecked={!!values?.startAt as boolean}
                value={values?.startAt}
                onChange={(value) => {
                  setFieldValue('startAt', value);
                }}
              >
                <RadioGroup.Label className="sr-only">
                  Available Times
                </RadioGroup.Label>
                <div
                  className={cn(
                    'flex',
                    'flex-col',
                    'self-stretch',
                    'gap-[8px]',
                    'max-h-[calc(42px*7)]',
                    'overflow-y-auto',
                    'p-1',
                  )}
                >
                  {availableTimes?.map((time) => {
                    const convertedTime = dayjs
                      .unix(time.unix)
                      .tz(time.bookingTimezone)
                      .format('hh:mm A');
                    const convertedLocalTime = dayjs
                      .unix(time.unix)
                      .tz(time.convertedTimezone)
                      .format('hh:mm A');
                    const label = (
                      <div>
                        <div>{convertedTime}</div>
                        <div className="text-[10px] italic font-normal tracking-wider text-content">
                          ( {time.convertedDate} {convertedLocalTime}{' '}
                          <strong>{time.convertedTimezone}</strong> )
                        </div>
                      </div>
                    );

                    return (
                      <RadioGroup.Option
                        key={time.unix}
                        value={time}
                        className="rounded-md"
                      >
                        {({ active, checked }) => (
                          <Button
                            size={'md'}
                            intent={active || checked ? 'primary' : 'tertiary'}
                            label={label}
                            className={cn('w-full')}
                          />
                        )}
                      </RadioGroup.Option>
                    );
                  })}
                </div>
              </RadioGroup>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-end justify-end ">
        <Button
          disabled={isSubmitting || !isValid}
          type="submit"
          label="NEXT"
          className="font-bold"
        />
      </div>
    </form>
  );
};

export default ClientBookingForm;
